import { Time, useNow } from "@commerce/components/time";
import { Badge } from "@ui/components/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import { timeLeft, windowState } from "./window-state";

/**
 * Whether the team can still write to a WhatsApp shopper, and for how long. Rendered once the
 * page is live: the window depends on now, which the server and the browser do not share.
 */
export function WindowChip({
	lastInboundAt,
	lastInboundCallAt,
	freeEntryEndsAt,
	className,
}: {
	lastInboundAt: Date | string | null;
	lastInboundCallAt?: Date | string | null;
	freeEntryEndsAt?: Date | string | null;
	className?: string;
}) {
	const nowMs = useNow();
	if (!nowMs) {
		return null;
	}
	const now = new Date(nowMs);
	const state = windowState({
		lastInboundAt: toDate(lastInboundAt),
		lastInboundCallAt: toDate(lastInboundCallAt),
		fepEndsAt: toDate(freeEntryEndsAt),
		now,
	});

	const { label, variant, help } =
		state.kind === "free_entry"
			? {
					label: `Free, ${timeLeft(state.endsAt.getTime() - nowMs)} left`,
					variant: "info" as const,
					help: "The shopper came from an ad, so every message is free until",
				}
			: state.kind === "open"
				? {
						label: state.closingSoon
							? `Closes in ${timeLeft(state.endsAt.getTime() - nowMs)}`
							: `Window open, ${timeLeft(state.endsAt.getTime() - nowMs)} left`,
						variant: state.closingSoon
							? ("warning" as const)
							: ("success" as const),
						help:
							state.by === "call"
								? "Their call opened the 24-hour window. You can write freely until"
								: "You can write freely until",
					}
				: {
						label: "Window closed",
						variant: "secondary" as const,
						help: "Only an approved template reaches the shopper now. It opens again when they write or call.",
					};

	return (
		<Tooltip>
			<TooltipTrigger
				render={
					<Badge
						variant={variant}
						className={cn("shrink-0", className)}
					/>
				}
			>
				{label}
			</TooltipTrigger>
			<TooltipContent className="max-w-64">
				{help}
				{state.kind === "closed" ? null : (
					<>
						{" "}
						<Time at={state.endsAt} as="full" />.
					</>
				)}
			</TooltipContent>
		</Tooltip>
	);
}

function toDate(value: Date | string | null | undefined): Date | null {
	return value ? new Date(value) : null;
}
